import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { HospitalReferralOutbox } from '@/components/patient/HospitalReferralOutbox';
import { DiagnosisField } from '@/components/patient/ReferralDiagnosisField';
import { ReferralFormChrome, Section } from '@/components/patient/ReferralFormLayout';
import { patientTabPath } from '@/lib/patientRecordPaths';
import { CodeChip } from '@/components/ui/Chip';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import type { SpecialistReferralPatient } from '@/components/patient/SpecialistReferralForm';
import { useReferrerIdentity } from '@/components/patient/useReferrerIdentity';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useClinic } from '@/hooks/useClinicSettings';
import { useJournalEntries } from '@/hooks/useJournal';
import { useDecryptCpr } from '@/hooks/usePatients';
import { useSorBySorId } from '@/hooks/useSorLookup';
import { ApiCallError, callFunction } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import {
  GGOP_RELATION_LABELS,
  ICF_GRADIENTS,
  emptyAddress,
  emptyContact,
  emptyPerson,
  ggopCcMax,
  ggopMissing,
  ggopPlanBody,
  splitName,
  type GgopContactDraft,
  type GgopFormMeta,
  type GgopFormState,
  type GgopPersonDraft,
  type GgopPhoneDraft,
  type GgopPlanType,
  type GgopSender,
} from '@/lib/ggop/ggopForm';
import { cprDigits, readPostalAddress } from '@/lib/referral/xref01Validation';
import { readTelephoneSubscribers } from '@/lib/patientStamkortFields';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { GgopPartyPicker } from './GgopPartyPicker';
import { GgopOutboxView } from './GgopOutboxView';
import { GgopViewer } from './GgopViewer';

// T2.4 (GGOP-S 3.3.x–3.6.x): "Genoptræningsplan" from the patient page. Builds GGOP1/2/3
// through communicate-ggop → Communicate (buildGgop, XSD check) and puts a valid plan in
// the GGOP outbox, where the VANSEnvelope is made and receipts couple to it (T2.5).
// Sending is blocked while a mandatory field for the chosen type is missing (the list is
// shown). The patient's CPR is read via decrypt-cpr and passed straight to the build.

type BuildResult = {
  id: string;
  outboxId: string | null;
  ok: boolean;
  envelopeIdentifier: string;
  letterIdentifier: string;
  planType: string;
  statisticalCode: string;
  checks: { id: string; pass: boolean; detail: string }[];
  xml: string;
};

const inputClass = cn(
  TYPE.inputValue,
  'block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ',
);

function TextBlock({ id, label, value, onChange, required, rows = 4 }: { id: string; label: string; value: string; onChange: (v: string) => void; required?: boolean; rows?: number }) {
  return (
    <div>
      <label htmlFor={id} className={cn('mb-1 block', FORM_LABEL_TYPE)}>
        {label}
        {required ? <span className="ml-1 text-ds-red-ink">*</span> : null}
      </label>
      <textarea id={id} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
    </div>
  );
}

function Select({ id, label, value, onChange, children }: { id: string; label: string; value: string; onChange: (v: string) => void; children: ReactNode }) {
  return (
    <label htmlFor={id} className="block">
      <span className={FORM_LABEL_TYPE}>{label}</span>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={cn('mt-1', inputClass)}>
        {children}
      </select>
    </label>
  );
}

function Radio<T extends string>({ name, value, options, onChange }: { name: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-4">
      {options.map((o) => (
        <label key={o.value} className={cn('inline-flex items-center gap-2', TYPE.body)}>
          <input type="radio" name={name} checked={value === o.value} onChange={() => onChange(o.value)} className="h-4 w-4" />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function Check({ label, checked, onChange, testId }: { label: string; checked: boolean; onChange: (v: boolean) => void; testId?: string }) {
  return (
    <label className={cn('inline-flex items-center gap-2', TYPE.body)}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded" data-testid={testId} />
      {label}
    </label>
  );
}

function PhonesEditor({ idPrefix, phones, codes, max, onChange }: { idPrefix: string; phones: GgopPhoneDraft[]; codes: readonly string[]; max: number; onChange: (p: GgopPhoneDraft[]) => void }) {
  const { t } = useTranslation();
  const labels: Record<string, string> = { mobil: 'Mobil', fastnet: 'Fastnet', arbejde: 'Arbejde' };
  return (
    <div className="space-y-2">
      {phones.map((p, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="w-36">
            <Select id={`${idPrefix}-code-${i}`} label={t('patients.ggop.phone_type', 'Type')} value={p.code} onChange={(code) => onChange(phones.map((x, j) => (j === i ? { ...x, code } : x)))}>
              {codes.map((c) => (
                <option key={c} value={c}>{labels[c] ?? c}</option>
              ))}
            </Select>
          </div>
          <div className="flex-1">
            <Input id={`${idPrefix}-number-${i}`} label={t('patients.ggop.phone', 'Telefonnummer')} value={p.number} onChange={(e) => onChange(phones.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))} />
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(phones.filter((_, j) => j !== i))} aria-label={t('patients.ggop.remove', 'Fjern')}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {phones.length < max && (
        <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...phones, { code: codes[0] ?? 'mobil', number: '' }])}>
          <Plus className="mr-1 h-4 w-4" />
          {t('patients.ggop.phone_add', 'Tilføj telefonnummer')}
        </Button>
      )}
    </div>
  );
}

function PersonEditor({ idPrefix, value, onChange }: { idPrefix: string; value: GgopPersonDraft; onChange: (v: GgopPersonDraft) => void }) {
  const { t } = useTranslation();
  const set = (k: keyof GgopPersonDraft) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Input id={`${idPrefix}-given`} label={t('patients.ggop.given_name', 'Fornavn')} value={value.givenName} onChange={set('givenName')} />
      <Input id={`${idPrefix}-surname`} label={t('patients.ggop.surname', 'Efternavn')} value={value.surname} onChange={set('surname')} />
      <Input id={`${idPrefix}-title`} label={t('patients.ggop.title', 'Titel')} value={value.title} onChange={set('title')} />
      <Input id={`${idPrefix}-dept`} label={t('patients.ggop.department', 'Afdeling')} value={value.departmentName} onChange={set('departmentName')} />
      <Input id={`${idPrefix}-phone`} label={t('patients.ggop.phone', 'Telefonnummer')} value={value.phone} onChange={set('phone')} />
    </div>
  );
}

function ContactEditor({ idPrefix, value, onChange }: { idPrefix: string; value: GgopContactDraft; onChange: (v: GgopContactDraft) => void }) {
  const { t } = useTranslation();
  const set = (k: keyof GgopContactDraft) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Input id={`${idPrefix}-dept`} label={t('patients.ggop.contact_department', 'Afdeling / enhed')} value={value.departmentName} onChange={set('departmentName')} />
      <Input id={`${idPrefix}-street`} label={t('patients.ggop.street', 'Adresse')} value={value.streetName} onChange={set('streetName')} />
      <Input id={`${idPrefix}-zip`} label={t('patients.ggop.post_code', 'Postnr.')} value={value.postCodeIdentifier} onChange={set('postCodeIdentifier')} />
      <Input id={`${idPrefix}-city`} label={t('patients.ggop.district', 'By')} value={value.districtName} onChange={set('districtName')} />
      <Input id={`${idPrefix}-phone`} label={t('patients.ggop.phone', 'Telefonnummer')} value={value.phone} onChange={set('phone')} />
      <Input id={`${idPrefix}-ean`} label={t('patients.ggop.ean_optional', 'EAN (valgfri)')} value={value.ean} onChange={set('ean')} />
    </div>
  );
}

function initialState(patient: SpecialistReferralPatient): GgopFormState {
  const address = readPostalAddress(patient.address);
  return {
    planType: 'almen',
    receiver: null,
    gpCopy: true,
    gpCcReceiver: null,
    ccReceivers: [],
    patient: {
      givenName: patient.first_name ?? '',
      surname: patient.last_name ?? '',
      streetName: address.street,
      suburbName: '',
      districtName: address.district,
      postCodeIdentifier: address.postCode,
      occupation: patient.job_title ?? '',
      email: patient.email ?? '',
      phones: readTelephoneSubscribers(patient.telephone_subscribers).map((s) => ({ number: s.identifier, code: s.code })),
      noTelephoneReason: '',
      coAddressOn: false,
      coAddress: emptyAddress(),
    },
    relativeMode: 'ikke_relevant',
    relative: { ...emptyAddress(), relationCode: '', phones: [], email: '' },
    signedBy: emptyPerson(),
    coWriterOn: false,
    coWriter: emptyPerson(),
    informationForPatientAndRelatives: '',
    consentInformed: false,
    practicalInformation: '',
    patientSafetyAspects: '',
    rehabilitationDeadline: '',
    contactStartDate: '',
    contactStartTime: '',
    contactEndMode: 'date',
    contactEndDate: '',
    contactEndTime: '',
    contactEndText: '',
    controlMode: 'none',
    controlDate: '',
    controlTime: '',
    controlText: '',
    controlLocation: null,
    patientHealthSummary: '',
    abilityAtDischargeSummary: '',
    extendedOn: false,
    abilityAtDischargeExtended: {},
    rehabilitationNeedAndPotential: '',
    icdMode: 'coded',
    icdPrimary: null,
    icdSecondary: [],
    icdText: '',
    icf: [],
    journalExcerpts: [],
    contactMunicipality: emptyContact(),
    contactCountyOn: false,
    contactCounty: emptyContact(),
    healthCareTeam: [],
  };
}

export function GgopForm({
  patient,
  revealedCpr,
  onCprRevealed,
  canRevealCpr,
  onCancel,
  onCreated,
}: {
  patient: SpecialistReferralPatient;
  revealedCpr: string | null;
  onCprRevealed: (cpr: string) => void;
  canRevealCpr: boolean;
  onCancel?: () => void;
  onCreated?: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clinicQuery = useClinic(patient.clinic_id);
  const connectionQuery = useClinicCommunicateConnection(patient.clinic_id);
  const senderSor = useSorBySorId(connectionQuery.data?.sor_id ?? null);
  const referrer = useReferrerIdentity(user?.id, patient.clinic_id);
  const journal = useJournalEntries(patient.clinic_id, { patientId: patient.id, limit: 100 });
  const decrypt = useDecryptCpr();
  const codesQuery = useQuery({
    queryKey: ['ggop-outbox-codes', patient.clinic_id, patient.id],
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase
        .from('clinic_communicate_outbound_referrals')
        .select('id, statistical_code')
        .eq('clinic_id', patient.clinic_id)
        .eq('patient_id', patient.id)
        .eq('kind', 'GGOP');
      if (error) throw error;
      return Object.fromEntries((data ?? []).filter((r) => r.statistical_code).map((r) => [r.id, r.statistical_code as string]));
    },
  });
  const metaQuery = useQuery({
    queryKey: ['ggop-form-meta', patient.clinic_id],
    staleTime: 10 * 60_000,
    queryFn: () => callFunction<{ action: 'form'; clinic_id: string }, GgopFormMeta>('communicate-ggop', { action: 'form', clinic_id: patient.clinic_id }),
  });

  const [state, setState] = useState<GgopFormState>(() => initialState(patient));
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [serverMissing, setServerMissing] = useState<{ key: string; label: string }[] | null>(null);
  const [result, setResult] = useState<BuildResult | null>(null);
  const [outboxRevision, setOutboxRevision] = useState(0);
  const [cprFailed, setCprFailed] = useState(false);
  const onCprRevealedRef = useRef(onCprRevealed);
  onCprRevealedRef.current = onCprRevealed;

  const set = <K extends keyof GgopFormState>(key: K, value: GgopFormState[K]) => setState((s) => ({ ...s, [key]: value }));
  const setPatient = (patch: Partial<GgopFormState['patient']>) => setState((s) => ({ ...s, patient: { ...s.patient, ...patch } }));

  // Underskrift: the signed-in user's name and title (same source as the referrals).
  useEffect(() => {
    if (!referrer.data) return;
    const name = splitName(referrer.data.name);
    setState((s) =>
      s.signedBy.surname || s.signedBy.givenName
        ? s
        : { ...s, signedBy: { ...s.signedBy, ...name, title: referrer.data?.title ?? '' } },
    );
  }, [referrer.data]);

  useEffect(() => {
    if (!patient.has_cpr || revealedCpr || !canRevealCpr) return;
    let cancelled = false;
    void decrypt.mutateAsync({ patient_id: patient.id, source: 'ggop-form', purpose: 'ggop' }).then(
      (r) => !cancelled && onCprRevealedRef.current(r.cpr),
      () => !cancelled && setCprFailed(true),
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRevealCpr, patient.has_cpr, patient.id, revealedCpr]);

  const meta = metaQuery.data;
  const clinic = clinicQuery.data;
  const clinicAddress = readPostalAddress(clinic?.address);
  const sender: GgopSender | null =
    clinic && connectionQuery.data?.ean && senderSor.data?.identifier && senderSor.data.identifierCode
      ? {
          ean: connectionQuery.data.ean,
          identifier: senderSor.data.identifier,
          identifierCode: senderSor.data.identifierCode,
          organisationName: clinic.name,
          ...(clinicAddress.street ? { streetName: clinicAddress.street } : {}),
          ...(clinicAddress.district ? { districtName: clinicAddress.district } : {}),
          ...(clinicAddress.postCode ? { postCodeIdentifier: clinicAddress.postCode } : {}),
          ...(clinic.phone ? { telephoneSubscriberIdentifier: clinic.phone } : {}),
        }
      : null;
  const cpr = revealedCpr ? cprDigits(revealedCpr) : null;
  const body = useMemo(
    () => (meta ? ggopPlanBody(state, { sender: sender ?? { ean: '', identifier: '', identifierCode: '', organisationName: '' }, cpr, meta }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, meta, cpr, sender?.ean, sender?.identifier, sender?.organisationName],
  );
  const missing = body && meta ? ggopMissing(body, meta) : [];
  const shownMissing = serverMissing ?? (attempted ? missing : []);
  const required = new Set(meta?.requiredFields[state.planType] ?? []);
  const ccMax = meta ? ggopCcMax(meta, state.gpCopy) : 0;
  const extendedShown = state.extendedOn || state.planType === 'rehabilitering';
  const label = (key: string, fallback: string) => meta?.fieldLabels[key] ?? fallback;

  async function onSubmit() {
    setAttempted(true);
    setSubmitError(null);
    setServerMissing(null);
    setResult(null);
    if (!body || !meta) return;
    if (!sender) {
      setSubmitError(t('patients.ggop.sender_missing', 'Afsenderen (klinikkens EAN og SOR-identifikator) er ikke tilgængelig.'));
      return;
    }
    if (missing.length > 0) return;
    setPending(true);
    try {
      const built = await callFunction<{ action: 'build'; clinic_id: string; patient_id: string; plan: Record<string, unknown> }, BuildResult>('communicate-ggop', {
        action: 'build',
        clinic_id: patient.clinic_id,
        patient_id: patient.id,
        plan: body as unknown as Record<string, unknown>,
      });
      setResult(built);
      if (built.outboxId) {
        setOutboxRevision((r) => r + 1);
        void codesQuery.refetch();
      }
      if (built.ok && built.outboxId) {
        onCreated?.();
      }
    } catch (err) {
      if (err instanceof ApiCallError) {
        const details = err.details as { missing?: { key: string; label: string }[] } | undefined;
        if (Array.isArray(details?.missing)) setServerMissing(details.missing);
        setSubmitError(err.message);
      } else {
        setSubmitError(t('patients.ggop.build_failed', 'Genoptræningsplanen kunne ikke dannes.'));
      }
    } finally {
      setPending(false);
    }
  }

  if (metaQuery.isLoading) {
    return (
      <div className="flex flex-col gap-s5" role="status">
        <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('patients.ggop.title_form', 'Genoptræningsplan')}</h1>
        <p className={cn(TYPE.body, 'text-ds-muted')}>
          {t('patients.ggop.loading_form', 'Henter genoptræningsplanens felter…')}
        </p>
        <Section>
          <Skeleton className="mb-s3 h-5 w-44" />
          <SkeletonText lines={3} />
        </Section>
        <Section>
          <Skeleton className="mb-s3 h-5 w-36" />
          <SkeletonText lines={3} />
        </Section>
        <Section>
          <Skeleton className="mb-s3 h-5 w-52" />
          <SkeletonText lines={3} />
        </Section>
      </div>
    );
  }
  if (!meta) {
    return <p role="alert" className={FORM_ERROR_TYPE}>{metaQuery.error instanceof Error ? metaQuery.error.message : t('patients.ggop.form_failed', 'Formularen kunne ikke hentes.')}</p>;
  }

  const planHeading = meta.planTypes.find((p) => p.planType === state.planType);
  const journalRows = journal.data ?? [];
  const pickedIds = new Set(state.journalExcerpts.map((j) => j.entryId));

  const patientName = `${patient.first_name} ${patient.last_name}`.trim();

  return (
    <div data-testid="ggop-form">
      <ReferralFormChrome
        backTo={patientTabPath(patient.id, 'medcom')}
        backLabel={t('patients.tabs.medcom')}
        title={t('patients.ggop.title_form', 'Genoptræningsplan')}
        subtitle={t('patients.ggop.form_subtitle', 'Genoptræningsplan for {{name}}', { name: patientName })}
        onCancel={onCancel}
        footer={
          <Button
            type="button"
            onClick={() => void onSubmit()}
            disabled={pending}
            loading={pending}
            data-testid="ggop-submit"
          >
            {t('patients.ggop.submit', 'Dan genoptræningsplan')}
          </Button>
        }
      >
      <p className={FORM_HINT_TYPE}>{t('patients.ggop.required_hint', 'Felter med * er obligatoriske for den valgte plantype.')}</p>

      <Section title={t('patients.ggop.type_section', 'Plantype')}>
        <div className="grid gap-s3 nav:grid-cols-3" role="radiogroup" data-testid="ggop-type">
          {meta.planTypes.map((p) => (
            <label
              key={p.planType}
              className={cn(
                'cursor-pointer rounded-pop border p-s4',
                state.planType === p.planType
                  ? 'border-ds-teal bg-ds-teal-tint'
                  : 'border-ds-line bg-ds-card',
              )}
            >
              <input type="radio" name="ggop-type" className="sr-only" checked={state.planType === p.planType} onChange={() => set('planType', p.planType as GgopPlanType)} />
              <span className={cn(TYPE.section, 'block text-ds-text')}>{p.heading}</span>
              <span className="mt-s2 inline-block">
                <CodeChip>{p.statisticalCode}</CodeChip>
              </span>
            </label>
          ))}
        </div>
      </Section>

      <Section title={t('patients.ggop.parties_section', 'Afsender og modtagere')}>
        <div className="space-y-4">
          <div className={TYPE.body}>
            <span className={FORM_LABEL_TYPE}>{t('patients.ggop.sender', 'Afsender')}: </span>
            {sender ? `${sender.organisationName} · EAN ${sender.ean} · ${sender.identifierCode} ${sender.identifier}` : t('patients.ggop.sender_loading', 'Indlæses…')}
          </div>
          <GgopPartyPicker
            fieldId="ggop-receiver"
            label={`${label('receiver', 'Modtager')} *`}
            value={state.receiver}
            onChange={(v) => {
              set('receiver', v);
              if (v && !state.contactMunicipality.departmentName && v.identifierCode !== 'ydernummer') {
                set('contactMunicipality', {
                  ...state.contactMunicipality,
                  departmentName: v.organisationName,
                  streetName: v.streetName ?? '',
                  postCodeIdentifier: v.postCodeIdentifier ?? '',
                  districtName: v.districtName ?? '',
                });
              }
            }}
            identifierCodes={meta.identifierCodes}
          />
          {required.has('municipalityReceiverOrCopy') && (
            <p className={FORM_HINT_TYPE}>{label('municipalityReceiverOrCopy', 'Kopi til bopælskommunen')}: {t('patients.ggop.municipality_hint', 'bopælskommunen skal være modtager eller kopimodtager (identifikatorkode kommunenummer).')}</p>
          )}
          <div className="space-y-2">
            <Check label={t('patients.ggop.gp_copy', 'Kopi til egen læge')} checked={state.gpCopy} onChange={(v) => setState((s) => ({ ...s, gpCopy: v, ccReceivers: s.ccReceivers.slice(0, ggopCcMax(meta, v)) }))} testId="ggop-gp-copy" />
            {state.gpCopy ? (
              <GgopPartyPicker fieldId="ggop-gp" label={t('patients.ggop.gp', 'Egen læge')} value={state.gpCcReceiver} onChange={(v) => set('gpCcReceiver', v)} identifierCodes={meta.identifierCodes} />
            ) : (
              <p className={FORM_HINT_TYPE}>{t('patients.ggop.gp_deselected', 'Kopi til egen læge er fravalgt.')}</p>
            )}
          </div>
          <div className="space-y-2">
            <div className={FORM_LABEL_TYPE}>{t('patients.ggop.cc', 'Øvrige kopimodtagere')} ({state.ccReceivers.length}/{ccMax})</div>
            {state.ccReceivers.map((cc, i) => (
              <GgopPartyPicker
                key={i}
                fieldId={`ggop-cc-${i}`}
                label={t('patients.ggop.cc_one', 'Kopimodtager {{n}}', { n: i + 1 })}
                value={cc}
                onChange={(v) => set('ccReceivers', v ? state.ccReceivers.map((x, j) => (j === i ? v : x)) : state.ccReceivers.filter((_, j) => j !== i))}
                identifierCodes={meta.identifierCodes}
                removeLabel={t('patients.ggop.cc_remove', 'Fjern kopimodtager')}
              />
            ))}
            {state.ccReceivers.length < ccMax && <CcAdder identifierCodes={meta.identifierCodes} index={state.ccReceivers.length} onAdd={(v) => set('ccReceivers', [...state.ccReceivers, v])} />}
          </div>
        </div>
      </Section>

      <Section title={`${label('patient', 'Patient')} *`}>
        <div className="space-y-4">
          <div className={TYPE.body}>
            CPR: {revealedCpr ? t('patients.ggop.cpr_ok', 'hentet (vises ikke her)') : cprFailed ? t('patients.ggop.cpr_failed', 'kunne ikke hentes') : patient.has_cpr ? t('patients.ggop.cpr_loading', 'hentes…') : t('patients.ggop.cpr_none', 'ikke registreret')}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input id="ggop-p-given" label={t('patients.ggop.given_name', 'Fornavn')} value={state.patient.givenName} onChange={(e) => setPatient({ givenName: e.target.value })} />
            <Input id="ggop-p-surname" label={t('patients.ggop.surname', 'Efternavn')} value={state.patient.surname} onChange={(e) => setPatient({ surname: e.target.value })} />
            <Input id="ggop-p-occ" label={t('patients.ggop.occupation', 'Stilling')} value={state.patient.occupation} onChange={(e) => setPatient({ occupation: e.target.value })} />
            <Input id="ggop-p-street" label={t('patients.ggop.street', 'Adresse')} value={state.patient.streetName} onChange={(e) => setPatient({ streetName: e.target.value })} />
            <Input id="ggop-p-zip" label={t('patients.ggop.post_code', 'Postnr.')} value={state.patient.postCodeIdentifier} onChange={(e) => setPatient({ postCodeIdentifier: e.target.value })} />
            <Input id="ggop-p-city" label={t('patients.ggop.district', 'By')} value={state.patient.districtName} onChange={(e) => setPatient({ districtName: e.target.value })} />
            <Input id="ggop-p-email" label={t('patients.ggop.email', 'E-mail')} value={state.patient.email} onChange={(e) => setPatient({ email: e.target.value })} />
          </div>
          <PhonesEditor idPrefix="ggop-p-phone" phones={state.patient.phones} codes={meta.phoneCodes} max={meta.limits.phones} onChange={(phones) => setPatient({ phones })} />
          {state.patient.phones.filter((p) => p.number.trim()).length === 0 && (
            <Input
              id="ggop-p-nophone"
              label={t('patients.ggop.no_phone_reason', 'Telefonnummer mangler – årsag')}
              value={state.patient.noTelephoneReason}
              onChange={(e) => setPatient({ noTelephoneReason: e.target.value })}
            />
          )}
          <Check label={t('patients.ggop.co_address', 'c/o-adresse')} checked={state.patient.coAddressOn} onChange={(v) => setPatient({ coAddressOn: v })} />
          {state.patient.coAddressOn && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Input id="ggop-co-given" label={t('patients.ggop.co_given', 'c/o fornavn')} value={state.patient.coAddress.givenName} onChange={(e) => setPatient({ coAddress: { ...state.patient.coAddress, givenName: e.target.value } })} />
              <Input id="ggop-co-surname" label={t('patients.ggop.co_surname', 'c/o efternavn')} value={state.patient.coAddress.surname} onChange={(e) => setPatient({ coAddress: { ...state.patient.coAddress, surname: e.target.value } })} />
              <Input id="ggop-co-street" label={t('patients.ggop.street', 'Adresse')} value={state.patient.coAddress.streetName} onChange={(e) => setPatient({ coAddress: { ...state.patient.coAddress, streetName: e.target.value } })} />
              <Input id="ggop-co-zip" label={t('patients.ggop.post_code', 'Postnr.')} value={state.patient.coAddress.postCodeIdentifier} onChange={(e) => setPatient({ coAddress: { ...state.patient.coAddress, postCodeIdentifier: e.target.value } })} />
              <Input id="ggop-co-city" label={t('patients.ggop.district', 'By')} value={state.patient.coAddress.districtName} onChange={(e) => setPatient({ coAddress: { ...state.patient.coAddress, districtName: e.target.value } })} />
            </div>
          )}
        </div>
      </Section>

      <Section title={t('patients.ggop.relatives', 'Pårørende')}>
        <div className="space-y-3">
          <Radio
            name="ggop-relative"
            value={state.relativeMode}
            options={[
              { value: 'ikke_relevant', label: t('patients.ggop.relative_not_relevant', 'Ikke relevant') },
              { value: 'relative', label: t('patients.ggop.relative_add', 'Pårørende') },
            ]}
            onChange={(v) => set('relativeMode', v)}
          />
          {state.relativeMode === 'relative' && (
            <div className="space-y-3">
              <Select id="ggop-rel-code" label={`${t('patients.ggop.relation', 'Relation')} *`} value={state.relative.relationCode} onChange={(v) => set('relative', { ...state.relative, relationCode: v })}>
                <option value="">{t('patients.ggop.relation_choose', 'Vælg relation')}</option>
                {meta.relationCodes.filter((c) => c !== 'ingen_relationer').map((c) => (
                  <option key={c} value={c}>{GGOP_RELATION_LABELS[c] ?? c}</option>
                ))}
              </Select>
              <div className="grid gap-3 sm:grid-cols-3">
                <Input id="ggop-rel-given" label={t('patients.ggop.given_name', 'Fornavn')} value={state.relative.givenName} onChange={(e) => set('relative', { ...state.relative, givenName: e.target.value })} />
                <Input id="ggop-rel-surname" label={`${t('patients.ggop.surname', 'Efternavn')} *`} value={state.relative.surname} onChange={(e) => set('relative', { ...state.relative, surname: e.target.value })} />
                <Input id="ggop-rel-email" label={t('patients.ggop.email', 'E-mail')} value={state.relative.email} onChange={(e) => set('relative', { ...state.relative, email: e.target.value })} />
                <Input id="ggop-rel-street" label={t('patients.ggop.street', 'Adresse')} value={state.relative.streetName} onChange={(e) => set('relative', { ...state.relative, streetName: e.target.value })} />
                <Input id="ggop-rel-zip" label={t('patients.ggop.post_code', 'Postnr.')} value={state.relative.postCodeIdentifier} onChange={(e) => set('relative', { ...state.relative, postCodeIdentifier: e.target.value })} />
                <Input id="ggop-rel-city" label={t('patients.ggop.district', 'By')} value={state.relative.districtName} onChange={(e) => set('relative', { ...state.relative, districtName: e.target.value })} />
              </div>
              <PhonesEditor idPrefix="ggop-rel-phone" phones={state.relative.phones} codes={meta.phoneCodes} max={meta.limits.phones} onChange={(phones) => set('relative', { ...state.relative, phones })} />
            </div>
          )}
        </div>
      </Section>

      <Section title={t('patients.ggop.information_section', 'Information og aftaler')}>
        <div className="space-y-4">
          <TextBlock id="ggop-info" label={label('informationForPatientAndRelatives', 'Information om behandling til patient og pårørende')} required={required.has('informationForPatientAndRelatives')} value={state.informationForPatientAndRelatives} onChange={(v) => set('informationForPatientAndRelatives', v)} />
          <Check label={`${label('consentInformed', 'Informeret samtykke')} *`} checked={state.consentInformed} onChange={(v) => set('consentInformed', v)} testId="ggop-consent" />
          <TextBlock id="ggop-practical" label="Praktiske oplysninger" value={state.practicalInformation} onChange={(v) => set('practicalInformation', v)} rows={3} />
          <TextBlock id="ggop-safety" label="Patientsikkerhedsmæssige forhold" value={state.patientSafetyAspects} onChange={(v) => set('patientSafetyAspects', v)} rows={3} />
          <TextBlock id="ggop-deadline" label="Tidsfrist for start af genoptræning" value={state.rehabilitationDeadline} onChange={(v) => set('rehabilitationDeadline', v)} rows={2} />
          <div>
            <div className={FORM_LABEL_TYPE}>Aktuel sygehuskontakt</div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <Input id="ggop-start-date" type="date" label={`${label('currentHospitalContactStart', 'Startdato for sygehuskontakt')} *`} value={state.contactStartDate} onChange={(e) => set('contactStartDate', e.target.value)} />
              <Input id="ggop-start-time" type="time" label={t('patients.ggop.time', 'Tidspunkt')} value={state.contactStartTime} onChange={(e) => set('contactStartTime', e.target.value)} />
            </div>
            <div className="mt-3 space-y-2">
              <div className={FORM_LABEL_TYPE}>{label('currentHospitalContactEnd', 'Forventet dato for afslutning af sygehuskontakt (eller fritekst)')} *</div>
              <Radio name="ggop-end" value={state.contactEndMode} options={[{ value: 'date', label: t('patients.ggop.as_date', 'Dato') }, { value: 'text', label: t('patients.ggop.as_text', 'Fritekst') }]} onChange={(v) => set('contactEndMode', v)} />
              {state.contactEndMode === 'date' ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input id="ggop-end-date" type="date" label={t('patients.ggop.date', 'Dato')} value={state.contactEndDate} onChange={(e) => set('contactEndDate', e.target.value)} />
                  <Input id="ggop-end-time" type="time" label={t('patients.ggop.time', 'Tidspunkt')} value={state.contactEndTime} onChange={(e) => set('contactEndTime', e.target.value)} />
                </div>
              ) : (
                <Input id="ggop-end-text" label={t('patients.ggop.free_text', 'Fritekst')} placeholder="Om ca. 14 dage" value={state.contactEndText} onChange={(e) => set('contactEndText', e.target.value)} />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <div className={FORM_LABEL_TYPE}>Aftaler om kontrol og opfølgning</div>
            <Radio
              name="ggop-control"
              value={state.controlMode}
              options={[
                { value: 'none', label: t('patients.ggop.control_none', 'Ingen') },
                { value: 'date', label: t('patients.ggop.as_date', 'Dato') },
                { value: 'text', label: t('patients.ggop.as_text', 'Fritekst') },
              ]}
              onChange={(v) => set('controlMode', v)}
            />
            {state.controlMode === 'date' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input id="ggop-control-date" type="date" label={t('patients.ggop.date', 'Dato')} value={state.controlDate} onChange={(e) => set('controlDate', e.target.value)} />
                <Input id="ggop-control-time" type="time" label={t('patients.ggop.time', 'Tidspunkt')} value={state.controlTime} onChange={(e) => set('controlTime', e.target.value)} />
              </div>
            )}
            {state.controlMode === 'text' && (
              <Input id="ggop-control-text" label={t('patients.ggop.free_text', 'Fritekst')} value={state.controlText} onChange={(e) => set('controlText', e.target.value)} />
            )}
            {state.controlMode !== 'none' && (
              <GgopPartyPicker fieldId="ggop-control-location" label={t('patients.ggop.control_location', 'Lokation for kontrol')} value={state.controlLocation} onChange={(v) => set('controlLocation', v)} identifierCodes={meta.identifierCodes} />
            )}
          </div>
        </div>
      </Section>

      <Section title={t('patients.ggop.health_section', 'Helbred og funktionsevne')}>
        <div className="space-y-4">
          <TextBlock id="ggop-health" label={label('patientHealthSummary', 'Helbredsforhold inkl. beskrivelse af behandlingsforløb - Sammenfatning')} required={required.has('patientHealthSummary')} value={state.patientHealthSummary} onChange={(v) => set('patientHealthSummary', v)} />
          <TextBlock id="ggop-ability" label={label('abilityAtDischargeSummary', 'Helbredsrelateret funktionsevne på udskrivningstidspunktet - Sammenfatning')} required={required.has('abilityAtDischargeSummary')} value={state.abilityAtDischargeSummary} onChange={(v) => set('abilityAtDischargeSummary', v)} />
          <div className="space-y-3 rounded-md border border-ds-line p-3">
            {state.planType === 'rehabilitering' ? (
              <div className={FORM_LABEL_TYPE}>{label('abilityAtDischargeExtended', 'Udvidet beskrivelse af helbredsrelateret funktionsevne på udskrivningstidspunktet')} *</div>
            ) : (
              <Check label={label('abilityAtDischargeExtended', 'Udvidet beskrivelse af helbredsrelateret funktionsevne på udskrivningstidspunktet')} checked={state.extendedOn} onChange={(v) => set('extendedOn', v)} testId="ggop-extended" />
            )}
            {extendedShown &&
              meta.abilityTemplate.map((group) => (
                <div key={group.heading} className="space-y-2">
                  <div className="font-semibold">{group.heading}</div>
                  {group.functions.map((fn) => (
                    <div key={fn}>
                      <label htmlFor={`ggop-ext-${fn}`} className={cn('mb-1 block italic', FORM_LABEL_TYPE)}>- {fn}</label>
                      <textarea
                        id={`ggop-ext-${fn}`}
                        rows={2}
                        className={inputClass}
                        value={state.abilityAtDischargeExtended[fn] ?? ''}
                        onChange={(e) => set('abilityAtDischargeExtended', { ...state.abilityAtDischargeExtended, [fn]: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
              ))}
          </div>
          <TextBlock id="ggop-need" label={label('rehabilitationNeedAndPotential', 'Patientens genoptræningsbehov og potentiale')} required={required.has('rehabilitationNeedAndPotential')} value={state.rehabilitationNeedAndPotential} onChange={(v) => set('rehabilitationNeedAndPotential', v)} />
        </div>
      </Section>

      <Section title={t('patients.ggop.diagnoses_section', 'Diagnoser')}>
        <div className="space-y-4">
          <div className={FORM_LABEL_TYPE}>{label('actionDiagnosis', 'Aktionsdiagnose')} *</div>
          <Radio name="ggop-icd-mode" value={state.icdMode} options={[{ value: 'coded', label: t('patients.ggop.icd_coded', 'ICD10-kode') }, { value: 'text', label: t('patients.ggop.as_text', 'Fritekst') }]} onChange={(v) => set('icdMode', v)} />
          {state.icdMode === 'coded' ? (
            <>
              <DiagnosisField
                inputId="ggop-icd-primary"
                label={label('actionDiagnosis', 'Aktionsdiagnose')}
                selected={state.icdPrimary ? { code: state.icdPrimary.code, textDa: state.icdPrimary.text } : null}
                onSelect={(d) => set('icdPrimary', { code: d.code, text: d.textDa })}
                onClear={() => set('icdPrimary', null)}
              />
              {state.icdSecondary.map((d, i) => (
                <DiagnosisField
                  key={i}
                  inputId={`ggop-icd-secondary-${i}`}
                  label={t('patients.ggop.secondary', 'Bidiagnose')}
                  selected={d.code ? { code: d.code, textDa: d.text } : null}
                  onSelect={(x) => set('icdSecondary', state.icdSecondary.map((y, j) => (j === i ? { code: x.code, text: x.textDa } : y)))}
                  onClear={() => set('icdSecondary', state.icdSecondary.filter((_, j) => j !== i))}
                />
              ))}
              <Button type="button" variant="secondary" size="sm" onClick={() => set('icdSecondary', [...state.icdSecondary, { code: '', text: '' }])}>
                <Plus className="mr-1 h-4 w-4" />
                {t('patients.ggop.secondary_add', 'Tilføj bidiagnose')}
              </Button>
              <p className={FORM_HINT_TYPE}>{t('patients.ggop.diagnosis_cut', 'Diagnoseteksten sendes med højst {{max}} tegn.', { max: meta.limits.diagnosisText })}</p>
            </>
          ) : (
            <Input id="ggop-icd-text" label={t('patients.ggop.free_text', 'Fritekst')} value={state.icdText} onChange={(e) => set('icdText', e.target.value)} />
          )}
          <div className="space-y-2">
            <div className={FORM_LABEL_TYPE}>{t('patients.ggop.icf', 'ICF-koder')}</div>
            {state.icf.map((c, i) => (
              <div key={i} className="grid items-end gap-2 sm:grid-cols-[8rem_1fr_12rem_auto]">
                <Input id={`ggop-icf-code-${i}`} label={t('patients.ggop.code', 'Kode')} value={c.code} onChange={(e) => set('icf', state.icf.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)))} />
                <Input id={`ggop-icf-text-${i}`} label={t('patients.ggop.text', 'Tekst')} value={c.text} onChange={(e) => set('icf', state.icf.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                <Select id={`ggop-icf-gradient-${i}`} label={t('patients.ggop.gradient', 'Gradient')} value={c.gradient} onChange={(v) => set('icf', state.icf.map((x, j) => (j === i ? { ...x, gradient: v } : x)))}>
                  <option value="">–</option>
                  {ICF_GRADIENTS.map((g) => (
                    <option key={g.code} value={g.code}>{g.text} ({g.code})</option>
                  ))}
                </Select>
                <Button type="button" variant="ghost" size="sm" onClick={() => set('icf', state.icf.filter((_, j) => j !== i))} aria-label={t('patients.ggop.remove', 'Fjern')}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={() => set('icf', [...state.icf, { code: '', text: '', gradient: '' }])}>
              <Plus className="mr-1 h-4 w-4" />
              {t('patients.ggop.icf_add', 'Tilføj ICF-kode')}
            </Button>
          </div>
        </div>
      </Section>

      <Section title={t('patients.ggop.journal_section', 'Uddrag fra journalen')}>
        <div className="space-y-2">
          <p className={FORM_HINT_TYPE}>
            {t('patients.ggop.journal_hint', 'Vælg notater; forfatter og dato følger med. Højst {{max}}.', { max: meta.limits.journalExcerpts })} ({state.journalExcerpts.length}/{meta.limits.journalExcerpts})
          </p>
          {journal.isLoading && <p className={FORM_HINT_TYPE}>{t('patients.ggop.journal_loading', 'Henter journalnotater…')}</p>}
          {!journal.isLoading && journalRows.length === 0 && <p className={FORM_HINT_TYPE}>{t('patients.ggop.journal_empty', 'Ingen journalnotater.')}</p>}
          <ul className="max-h-72 space-y-1 overflow-auto">
            {journalRows.map((entry) => {
              const picked = pickedIds.has(entry.id);
              const full = !picked && state.journalExcerpts.length >= meta.limits.journalExcerpts;
              return (
                <li key={entry.id}>
                  <label className={cn('flex items-start gap-2 rounded-md border border-ds-line p-2 ', full && 'opacity-50')}>
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={picked}
                      disabled={full}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          set('journalExcerpts', state.journalExcerpts.filter((j) => j.entryId !== entry.id));
                          return;
                        }
                        const author = splitName(entry.author_name);
                        set('journalExcerpts', [
                          ...state.journalExcerpts,
                          {
                            entryId: entry.id,
                            typeOfExcerpt: entry.title || entry.template_title_snapshot || 'Journalnotat',
                            signer: author.surname ? { ...emptyPerson(), ...author, title: entry.clinician_title_snapshot ?? '' } : null,
                            date: entry.entry_date,
                            comment: entry.content_md,
                          },
                        ]);
                      }}
                    />
                    <span className={TYPE.body}>
                      <span className="font-medium">{entry.title || entry.template_title_snapshot || 'Journalnotat'}</span>
                      <span className="ml-2 text-xs text-ds-muted">{entry.entry_date} · {entry.author_name ?? '–'}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      </Section>

      <Section title={t('patients.ggop.people_section', 'Underskrift, medforfatter og team')}>
        <div className="space-y-4">
          <div>
            <div className={FORM_LABEL_TYPE}>{label('signedBy', 'Underskrift')} *</div>
            <div className="mt-2"><PersonEditor idPrefix="ggop-signed" value={state.signedBy} onChange={(v) => set('signedBy', v)} /></div>
          </div>
          <Check label={t('patients.ggop.co_writer', 'Medforfatter')} checked={state.coWriterOn} onChange={(v) => set('coWriterOn', v)} />
          {state.coWriterOn && <PersonEditor idPrefix="ggop-cowriter" value={state.coWriter} onChange={(v) => set('coWriter', v)} />}
          <div className="space-y-2">
            <div className={FORM_LABEL_TYPE}>{t('patients.ggop.team', 'Behandlerteam')} ({state.healthCareTeam.length}/{meta.limits.healthCareTeam})</div>
            {state.healthCareTeam.map((m, i) => (
              <div key={i} className="space-y-1 rounded-md border border-ds-line p-2">
                <PersonEditor idPrefix={`ggop-team-${i}`} value={m} onChange={(v) => set('healthCareTeam', state.healthCareTeam.map((x, j) => (j === i ? v : x)))} />
                <Button type="button" variant="ghost" size="sm" onClick={() => set('healthCareTeam', state.healthCareTeam.filter((_, j) => j !== i))}>
                  {t('patients.ggop.remove', 'Fjern')}
                </Button>
              </div>
            ))}
            {state.healthCareTeam.length < meta.limits.healthCareTeam && (
              <Button type="button" variant="secondary" size="sm" onClick={() => set('healthCareTeam', [...state.healthCareTeam, emptyPerson()])}>
                <Plus className="mr-1 h-4 w-4" />
                {t('patients.ggop.team_add', 'Tilføj teammedlem')}
              </Button>
            )}
          </div>
        </div>
      </Section>

      <Section title={t('patients.ggop.contacts_section', 'Kontaktoplysninger for patienten')}>
        <div className="space-y-4">
          <div>
            <div className={FORM_LABEL_TYPE}>{label('contactMunicipality', 'Kontaktoplysninger for patienten: Spørgsmål til Kommunen')} *</div>
            <div className="mt-2"><ContactEditor idPrefix="ggop-cm" value={state.contactMunicipality} onChange={(v) => set('contactMunicipality', v)} /></div>
          </div>
          <Check label={t('patients.ggop.contact_county', 'Kontaktoplysninger: Spørgsmål til Regionen')} checked={state.contactCountyOn} onChange={(v) => set('contactCountyOn', v)} />
          {state.contactCountyOn && <ContactEditor idPrefix="ggop-cc" value={state.contactCounty} onChange={(v) => set('contactCounty', v)} />}
        </div>
      </Section>

        {shownMissing.length > 0 && (
          <div role="alert" className="rounded-pop border border-ds-red-line bg-ds-red-tint p-s4" data-testid="ggop-missing">
            <p className={FORM_LABEL_TYPE}>
              {t('patients.ggop.missing_title', '{{heading}} kan ikke dannes. Disse obligatoriske felter mangler:', { heading: planHeading?.heading ?? '' })}
            </p>
            <ul className={cn('mt-s1 list-disc pl-5', FORM_ERROR_TYPE)}>
              {shownMissing.map((m) => (
                <li key={m.key}>{m.label}</li>
              ))}
            </ul>
          </div>
        )}
        {submitError && shownMissing.length === 0 && <p role="alert" className={cn('whitespace-pre-wrap', FORM_ERROR_TYPE)}>{submitError}</p>}
      </ReferralFormChrome>

      <div className="mt-s5 flex flex-col gap-s5">
      {result && (
        <div className="space-y-s3 rounded-card bg-ds-card p-s5 shadow-card" data-testid="ggop-result">
          <p className={cn(FORM_LABEL_TYPE, result.ok ? 'text-ds-green-ink' : 'text-ds-red-ink')}>
            {result.ok
              ? t('patients.ggop.built_ok', '{{code}} er dannet og lagt i udbakken (brev-id {{letter}}).', { code: result.statisticalCode, letter: result.letterIdentifier })
              : t('patients.ggop.built_failed', '{{code}} blev ikke dannet: et tjek fejlede.', { code: result.statisticalCode })}
          </p>
          <ul className="space-y-s1">
            {result.checks.map((c) => (
              <li key={c.id} className={cn(TYPE.body, c.pass ? 'text-ds-text' : 'text-ds-red-ink')}>
                {c.pass ? '✓' : '✗'} {c.detail}
              </li>
            ))}
          </ul>
          <GgopViewer xml={result.xml} planType={result.planType} patientId={patient.id} patientName={[state.patient.givenName, state.patient.surname].filter(Boolean).join(' ')} canRevealCpr={canRevealCpr} />
        </div>
      )}

      <HospitalReferralOutbox
        clinicId={patient.clinic_id}
        patientId={patient.id}
        revision={outboxRevision}
        kinds={['GGOP']}
        title={t('patients.ggop.outbox_title', 'Udbakke – genoptræningsplaner')}
        emptyText={t('patients.ggop.outbox_empty', 'Ingen genoptræningsplaner i udbakken.')}
        rowExtra={(row) => (
          <>
            {codesQuery.data?.[row.id] && (
              <p className={cn('mt-s2', TYPE.body)} data-testid="ggop-outbox-code">
                <CodeChip>{codesQuery.data[row.id]}</CodeChip>{' '}
                {meta.planTypes.find((p) => p.statisticalCode === codesQuery.data?.[row.id])?.heading ?? ''}
              </p>
            )}
            <GgopOutboxView
            clinicId={patient.clinic_id}
            exchangeId={row.exchange_id}
            patientName={[patient.first_name, patient.last_name].filter(Boolean).join(' ')}
            canRevealCpr={canRevealCpr}
          />
          </>
        )}
      />
      </div>
    </div>
  );
}

function CcAdder({ identifierCodes, index, onAdd }: { identifierCodes: readonly string[]; index: number; onAdd: (v: NonNullable<GgopFormState['receiver']>) => void }) {
  const { t } = useTranslation();
  return (
    <GgopPartyPicker
      fieldId={`ggop-cc-new-${index}`}
      label={t('patients.ggop.cc_add', 'Tilføj kopimodtager')}
      value={null}
      onChange={(v) => v && onAdd(v)}
      identifierCodes={identifierCodes}
    />
  );
}
